import { ButtonProps } from 'antd';
import { SortOrder } from 'antd/es/table/interface';
import { LocaleKeys } from './locales/allLocaleKeys';

export enum StorageProviders {
	ShareFile = 'ShareFile',
}

export type SortingInfo = {
	column: string;
	sortDirection?: SortOrder;
};

export type LegacyApiColumns =
	| 'FileSize'
	| 'CreatorNameShort'
	| 'Name'
	| 'ProgenyEditDate'
	| 'CreationDate';

export type LegacyApiSortOrder = 'asc' | 'desc';

export const columnMap: Record<LegacyApiColumns, string> = {
	FileSize: 'SizeInBytes',
	CreatorNameShort: 'CreatedByRID',
	Name: 'Name',
	ProgenyEditDate: 'LastModifiedDate',
	CreationDate: 'CreatedDate',
};

export const sortingOrderMap: Record<LegacyApiSortOrder, SortOrder> = {
	asc: 'ascend',
	desc: 'descend',
};

export enum DeletedResourceRetrievalType {
	ExcludeDeleted = 0,
	IncludeDeleted = 1,
	OnlyDeleted = 2,
}

export enum ResourceType {
	None = 0,
	File = 1,
	Folder = 2,
}

export enum ResourceTypeLiteral {
	File = 'file',
	Folder = 'folder',
	LegacyFolder = 'lfolder',
	LegacyFile = 'lfile',
	FileVersion = 'filever',
	LegacyFileVersion = 'lfilever',
}

export type ResourceFetchOptions = {
	deletedResourceRetrievalType?: DeletedResourceRetrievalType;
	tags?: string[];
	tagFilters?: TagFilters;
	subResourceFilter?: SubResourceFilter;
};

export interface PaginatedResponse<T> {
	resources: T[];
	totalRecords: number;
}

type FileResourceDTOLocalUploadFields = {
	isLocalUpload?: boolean;
	percent?: number;
	uploadError?: boolean;
	isUploadConflict?: boolean;
};

export interface PreviewSpecResponse {
	previewIdentifier: string;
	fetchPreviewUrlStatus: ErrorCodes;
}

export interface PreviewSpecV2Response {
	rid?: string;
	fileName: string;
	fileSize: number;
	eTag: string;
	createdDate: Date;
	lastModifiedDate: Date;
	accessUrl: string;
	viewOnly: boolean;
	fetchPreviewUrlStatus: ErrorCodes;
	encryptionKeyId?: string;
	watermarkText?: string;
	signature?: string;
	signatureKeyId?: string;
}

export type FileDeletionResponse = {
	rid: string;
	deletionStatus: ErrorCodes;
};

export type TopFolderStructureResponse = {
	rid: string;
	name: string;
};

export type ResourceCreationResponse = {
	creationStatus: ErrorCodes;
	resource: FileResourceDTO;
};

type FileResourceDTOInternalFields = {
	rid: string;
	isSoftLink: boolean;
	etag: string;
	isContainer?: boolean;
	permissions: {
		canRead: boolean;
		canCreate: boolean;
		canModify: boolean;
		canShare: boolean;
		canDelete: boolean;
		canDownload: boolean;
		canEditIfContainerCapabilitiesAllows: boolean;
	};
	malwareInfo?: MalwareInfo;
	isVersioned?: boolean;
	isCalculated?: boolean;
	tags?: Tag[];
	editUrl?: string;
	isFrozen?: boolean;
	storageZoneRID?: string;
	description?: string;
};

export type FileResourceDTODisplayFields = {
	name: string;
	sizeInBytes: number;
	createdByRID: string;
	createdDate: Date;
	lastModifiedByRID?: string;
	lastModifiedDate?: Date;
	deletedByRID?: string;
	deletedDate?: Date;
	containerTitle?: string;
};

export type MalwareInfo = {
	virusStatus: VirusStatus;
	virusName: string;
};

export enum VirusStatus {
	Infected = -1,
	Unscanned = 0,
	Scanned = 1,
	MarkedAsFalsePositive = 2,
}

export enum VirusStrictness {
	WarnOnly = 0,
	BlockInfected = 1,
	BlockUnscannedAndInfected = 2,
}

export type FileResourceDTO = FileResourceDTOInternalFields &
	FileResourceDTODisplayFields &
	FileResourceDTOLocalUploadFields;

export type OptionalColumnTypes =
	| 'addedIn'
	| 'type'
	| 'createdBy'
	| 'createdDate'
	| 'lastModifiedDate'
	| 'lastModifiedBy'
	| 'deletedDate'
	| 'deletedBy'
	| 'size';

export type OptionalColumns = Partial<Record<OptionalColumnTypes, boolean>>;

export type Languages = 'en' | 'es' | 'de' | 'fr' | 'it' | 'ja' | 'nl' | 'pt-BR';
export type TranslatedStrings = Partial<Record<Languages, string>>;
export type Locale<T> = {
	key: T;
	value: TranslatedStrings;
};

export type Pagination = {
	//To Load only n items, set this onlyDisplayFirstPage to true and defaultPageSize to n
	onlyDisplayFirstPage?: boolean;
	allowedPageSizes?: number[];
	defaultPageSize?: number;
	enableInfiniteScroll?: boolean;
	scrollBatchSize?: number;
};

export type ContextMenuItem = CustomizableButtonProps & {
	menuType: ContextMenuActionType;
	showIconOnly?: boolean;
};

export type CustomizableButtonProps = Pick<ButtonProps, 'type' | 'size'>;

export type ContextMenuActionType =
	| Exclude<ContextBarMenuActionType, 'reset'>
	| ContextBarMenuActionButtons;

export type ContextBarMenuActionType =
	| 'download'
	| 'preview'
	| 'rename'
	| 'delete'
	| 'share'
	| 'manage_users'
	| 'export'
	| 'reset'
	| 'send_for_signature'
	| 'send_for_approval'
	| 'edit_online'
	| 'edit_in_desktop'
	| 'open_folder_location'
	| 'move'
	| 'copy'
	| 'add_note';

export type ContextBarMenuActionButtons =
	| ContextBarMenuActionUpload
	| ContextBarMenuActionDownloadAll
	| ContextBarMenuActionCreate
	| ContextBarMenuActionImportFromShareFile
	| ContextBarMenuActionCreateOfficeDocs;
export type ContextBarMenuActionUpload = 'upload';
export type ContextBarMenuActionImportFromShareFile = 'import_from_ShareFile';
export type ContextBarMenuActionDownloadAll = 'downloadAll';
export type ContextBarMenuActionCreate = 'createFolder';
export type ContextBarMenuActionCreateOfficeDocs = 'createOfficeDocs';

export type ContainersStyle = {
	scrollableContainerStyle?: React.CSSProperties;
	fileViewerZIndex?: number;
};

// This will grow as more customizations are added in the pilet
// TODO: Add SortingInfo here in CustomizationData
export type CustomizationData = {
	columns?: OptionalColumns;
	columnsList?: OptionalColumnTypes[];
	contextMenu?: ContextMenuItem[];
	disableSelection?: boolean;
	disableTableHeader?: boolean;
	disableColumnSorting?: boolean;
	disableColumnFiltering?: boolean;
	disableOverallUploadProgress?: boolean;
	pagination?: Pagination;
	labels?: Locale<LocaleKeys>[];
	relatedResourcesForShare?: RelatedResource[];
	styles?: ContainersStyle;
	singleResourceAutoPreview?: boolean;
	sideLoadByAssociation?: SideLoadByAssociation;
	selectLoadedResourcesByDefault?: boolean;
	hideAllActionsOnHover?: boolean;
	analyticPrefix?: string;
	allowOnlySingleResourceSelection?: boolean;
};

export type ResourceFilter = {
	subContainerRIDs?: string[];
	tagFilters?: TagFilters;
	subResourceFilter?: SubResourceFilter;
	ShowSystemFiles?: boolean;
};

export type ResourceEditableFilter = Omit<ResourceFilter, 'tagFilters'>;

export enum ErrorCodes {
	None = 0,
	DuplicateFileName = 30001,
	ResourceNotFound = 30002,
	EtagMisMatch = 30003,
	IEnumerableResourceIdsExpected = 30004,
	InvalidRID = 30005,
	ResourceCreationFailed = 30006,
	AliasAlreadyExists = 30007,
	AliasCreationFailed = 30008,
	Unauthorized = 30009,
	ContainerRidNotSupported = 30010,
	StorageConfigurationMissing = 30011,
	ItemAlreadyExists = 30012,
	SourceItemNotFound = 30013,
	ErrorCreatingSourceItem = 30014,
	BulkDownloadNotSupported = 30015,
	ErrorFetchingDownloadUrl = 30016,
	InsufficientStorage = 30017,
	ErrorModifyingExistingItem = 30018,
	ErrorFetchingUser = 30019,
	UserNotFound = 30020,
	ErrorRestoringDeletedItem = 30021,
	RidNotSupported = 30022,
	ErrorSyncingUser = 30023,
	InvalidResourceName = 30024,
	ParentStorageConfigurationMissing = 30025,
	InfectedResource = 30026,
	UnscannedResource = 30027,
	DuplicateTagging = 30028,
	DeletionFailedAtExternalStorage = 30029,
	RestorationFailedAtExternalStorage = 30030,
	InvalidSortParameter = 30031,
	ResourceTypeNotSupported = 30032,
	EmptyFolderDownloadNotSupported = 30033,
	FileDoesNotExist = 30034,
	OverwriteOnVersionNotAllowed = 30035,
	VersioningNotEnabledAtContainer = 30036,
	VersioningNotEnabledAtAccount = 30037,
	InvalidSubResourceFilter = 30039,
	AliasResourceModificationNotAllowed = 30040,
	Frozen = 30041,
	EditingNotEnabledOnAccount = 30042,
	ExtensionNotSupportedForEditing = 30043,
	ResourceLockNotFound = 30044,
	PatchingResourceLockFailed = 30045,
	ErrorFetchingBootstrapperToken = 30046,
	InvalidExternalStorageProviderResourceId = 30047,
	ResourceCheckedOutOnStorageProvider = 30048,
	StorageZoneNotSupported = 30049,
	InvalidStorageZoneRID = 30050,
	InternalServerError = 30051,
	ItemMoveFailed = 30052,
	ItemMoveStatusUnknown = 30053,
	OperationFailed = 30054,
	OperationPending = 30055,
	OperationSucceededPartially = 30056,
	MultiplePrivateZonesConflict = 30057,
}

export type ImportResponse = {
	rid: string;
	status: ErrorCodes;
	addedResource?: FileResourceDTO;
};

export type UserInfoResponse = {
	rid: string;
	name: string;
	getUserInfoStatus: ErrorCodes;
};

export interface FileRestoreResponse {
	rid: string;
	restorationStatus: ErrorCodes;
}

export interface RelatedResource {
	rid: string;
	permissions?: string[];
	required?: boolean | null;
	relationshipLabel?: string | null;
	relationshipDescription?: string | null;
	isRequiredWhenOneOfSelectedPermissions?: string[];
}

export type NewStateFromPrevious = (
	prevState: PaginatedResponse<FileResourceDTO>
) => PaginatedResponse<FileResourceDTO>;

export type PaginatedDataModificationCallback = (
	prevState: PaginatedResponse<FileResourceDTO>,
	newState: PaginatedResponse<FileResourceDTO>
) => void;

export type TagFilters = {
	includeTags?: Tag[];
	excludeTags?: Tag[];
};

export type SubResourceFilter = {
	includeResourceRids?: string[];
};

export type Tag = {
	name: string;
	value: string;
};

export enum UserOperation {
	Add = 0,
	Update = 1,
	Delete = 2,
}

export type UserPermissions = {
	CanUpload: boolean;
	CanDownload: boolean;
	CanDelete: boolean;
	NotifyOnDownload: boolean;
	NotifyOnUpload: boolean;
};

export type SideLoadByAssociation = {
	byContainerRIDs?: string[];
	byTags?: TagFilters;
};
export type FileViewerPiletSpec = PreviewSpecV2Response & {
	fileRID: string;
	isBackButtonDisabled?: boolean;
	canEdit?: boolean;
	canDelete?: boolean;
	onClose?: () => void;
};

export interface ButtonExtensionProps {
	hideText?: boolean;
	disable?: boolean;
	hideIcon?: boolean;
	sideLoadByAssociation?: SideLoadByAssociation;
}
export type LinkToContainersParams = {
	versionAgnosticId: string;
	versionID: string;
	parentID: string;
};

export type ResourceIdsWithPermissions = Pick<
	FileResourceDTOInternalFields,
	'rid' | 'permissions'
>;

export type StorageZones = {
	accountStorageZones: StorageZone[];
};

export type StorageZone = {
	rid: string;
	name: string;
	category: StorageZoneCategory;
	isDefault: boolean;
};

export enum StorageZoneCategory {
	PublicZone = 'PublicZone',
	PrivateZone = 'PrivateZone',
	MultiTenantZone = 'MultiTenantZone',
}
export enum OperationType {
	Move,
	Copy,
}
export enum OperationStatus {
	Pending,
	Success,
	PartialSuccess,
	Unauthorized,
	Failed,
	RequestValidationFailed,
	InvalidSourceRIDs,
	OlderVersionRIDs,
	Continue,
	StorageProviderMismatch,
	StorageConfigurationNotFound,
	FrozenContainer,
	InternalServerError,
	FrozenSourceItem,
	FailedToGetSourceMetadata,
	FailedToCreateMoveCopyOperation,
}

export type OperationResponse = {
	operationId: string;
	type: OperationType;
	status: OperationStatus;
};

export type OperationItemResponse = {
	operationItemId: string;
	operationId: string;
	sourceRID: string;
	destinationRID: string;
	status: OperationItemStatus;
	name?: string;
};

export type OperationAPIResponse = {
	operations: OperationResponse[];
	operationItems: OperationItemResponse[];
};

export type MoveCopyStatusInfo = {
	operationItemId: string;
	operationId: string;
	sourceRID: string;
	destinationRID: string;
	operationItemStatus: OperationItemStatus;
	name?: string;
	createdDate?: Date;
	type?: OperationType;
	operationStatus?: OperationStatus;
};

export enum OperationItemStatus {
	Pending,
	Success,
	Error,
}

export type EventPayload = {
	count?: number;
	targetContainerRID?: string;
	newItemRIDs?: string[];
};
